import { useState } from 'preact/hooks'
import clsx from 'clsx'

import { useAthosContext } from '../../fetchers/provider'
import { useBeaconLogImpressions, useBeaconLogRender } from '../../hooks/useBeaconAnalytics'
import { ResultsCard } from '../ResultCard'
import { SkeletonCard } from '../SkeletonCard'

// Wishlist buttons need the PLP's Omneo context, so recommendation cards never render one.
const NO_WISHLIST = { type: 'none' }

// The standard card otherwise asks for half the viewport, which is wrong inside the 560px
// drawer. Mobile keeps the old value; desktop is capped to the widest cell the drawer shows.
const CARD_IMAGE_SIZES = '(min-width: 1024px) 440px, 50vw'
const CARD_IMAGE_WIDTHS = [240, 320, 480, 640, 880]

export function BaseResults({
  className,
  wrapperClassName = '',
  productCardSettings,
  renderWrapper,
  CardComponent = ResultsCard,
  SkeletonComponent = SkeletonCard
}) {
  const { results, isLoading } = useAthosContext()
  const [node, setNode] = useState()

  useBeaconLogRender()
  useBeaconLogImpressions(node)

  if (results?.results?.length === 0 && !isLoading) return null

  const placeholderCount = results?.results?.length || 8
  const items = Array.from({ length: placeholderCount }, (_, index) => ({
    id: index,
    result: results?.results?.[index]
  }))
  const cardOptions = productCardSettings ? { options: productCardSettings } : {}

  const renderCard = (id, result) => (
    <CardComponent
      result={result}
      className="wk-wishlist-button-target"
      index={id}
      dataIndex={String(id)}
      wishlist={NO_WISHLIST}
      imageSizes={CARD_IMAGE_SIZES}
      imageWidths={CARD_IMAGE_WIDTHS}
      {...cardOptions}
    />
  )

  const renderSkeletonItems = () =>
    items.map((_, index) => (
      <div className={clsx(className, 'shrink-0')} key={`skeleton-${index}`}>
        <SkeletonComponent {...cardOptions} />
      </div>
    ))

  const renderRealItems = () =>
    items.map(({ id, result }) => (
      <div className={clsx(className, 'shrink-0')} key={result?.handle || id}>
        {result && renderCard(id, result)}
      </div>
    ))

  const renderGridItems = () =>
    items.map(({ id, result }) => (
      <div className={clsx(className, 'shrink-0')} key={result?.handle || id}>
        {result ? renderCard(id, result) : <SkeletonComponent {...cardOptions} />}
      </div>
    ))

  return (
    <div ref={setNode} className={clsx('relative flex w-full', wrapperClassName)}>
      {renderWrapper ? renderWrapper(renderSkeletonItems(), renderRealItems()) : renderGridItems()}
    </div>
  )
}
